import { Dispatch, SetStateAction, createContext, useReducer, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "go-security/services/users/selectors";
import { UserState } from "go-security/services/users/types";
import { DashboardFetchSegments } from "./components/DashboardFetchSegments";
import { dashboardReducer } from "./services/dashboardContext";
import {
	DASHBOARD_EXTENDED_SM_LAYOUT,
	ORGANIZATION_DASHBOARD_DEFAULT_LG_LAYOUT,
	ORGANIZATION_DASHBOARD_GENERAL_SM_LAYOUT,
	USER_DASHBOARD_DEFAULT_LG_LAYOUT,
	USER_DASHBOARD_GENERAL_SM_LAYOUT,
} from "./services/segments/constants";
import { DashboardInitialStateType, DashboardType } from "./services/types";
import { getDefaultDashboardSegment } from "./utils/utils";

export interface DashboardContextApi {
	state: DashboardInitialStateType;
	dispatch: Dispatch<any>;
	dashboardType: DashboardType;
	isGridResettingMode: boolean;
	setIsGridResettingMode: (isResettingMode: boolean) => void;
	isEditMode: boolean;
	setIsEditMode: Dispatch<SetStateAction<boolean>>;
}

const getInitialConfig = ({ user }: { user?: UserState }) => {
	const isGroupDashboard = window.location.pathname.includes("/dashboard");
	const defaultDashboardSegment = getDefaultDashboardSegment(isGroupDashboard, user);

	return {
		data: undefined,
		filtersConfig: defaultDashboardSegment.value.filtersConfig,
		chartsConfig: defaultDashboardSegment.value.chartsConfig,
		dashboardEdit: {
			...defaultDashboardSegment.value.dashboardEdit,
			mobileGridConfig: {
				general: isGroupDashboard ? USER_DASHBOARD_GENERAL_SM_LAYOUT : ORGANIZATION_DASHBOARD_GENERAL_SM_LAYOUT,
				extended: DASHBOARD_EXTENDED_SM_LAYOUT,
			},
			nonMobileGridConfig: isGroupDashboard
				? USER_DASHBOARD_DEFAULT_LG_LAYOUT
				: ORGANIZATION_DASHBOARD_DEFAULT_LG_LAYOUT,
		},
		segmentsConfig: {
			allSegments: [defaultDashboardSegment],
			selectedSegment: defaultDashboardSegment,
		},
	};
};

export const DashboardContext = createContext<DashboardContextApi>({
	state: getInitialConfig({}),
	dispatch: () => null,
	dashboardType: DashboardType.ORGANIZATION,
	isGridResettingMode: false,
	setIsGridResettingMode: (isResettingMode: SetStateAction<boolean>) => {},
	isEditMode: false,
	setIsEditMode: (isEditMode: SetStateAction<boolean>) => {},
});

export const DashboardNewPage = ({ dashboardType }: { dashboardType: DashboardType }): JSX.Element => {
	const user = useSelector(selectUser);
	const [state, dispatch] = useReducer(dashboardReducer, { user }, getInitialConfig);
	const [isGridResettingMode, setIsGridResettingMode] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);

	return (
		<DashboardContext.Provider
			value={{
				state,
				dispatch,
				dashboardType,
				isGridResettingMode,
				setIsGridResettingMode,
				isEditMode,
				setIsEditMode,
			}}
		>
			<DashboardFetchSegments />
		</DashboardContext.Provider>
	);
};
